import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, ActivityIndicator, Alert, Platform } from 'react-native';
import type { StaffMembership } from '../types/staff';
import { parseCustomerPickupPrefs, type CustomerPickupBusinessPrefs } from '../types/customerPickupSettings';
import { appointmentManager, type Appointment } from '../utils/appointmentManager';
import { groupAppointmentsIntoDateSections } from '../utils/appointmentFilterUtils';
import { AppointmentPrepaidBadge } from '../components/AppointmentPrepaidBadge';
import UserBottomMenu from '../components/UserBottomMenu';
import LiveQueueCard from '../components/LiveQueueCard';

const toLocalDateKey = (dateInput: string): string => {
  if (!dateInput) return '';
  const match = dateInput.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) return dateInput;
  const dt = new Date(dateInput);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

interface Props {
  onBack: () => void;
  onTabPress: (tab: 'dashboard' | 'appointments' | 'work' | 'favorites' | 'profile') => void;
  onLogout: () => void;
  memberships: StaffMembership[];
  activeMembership: StaffMembership | null;
  onMembershipChange: (m: StaffMembership | null) => void;
}

const WorkAppointmentsScreen: React.FC<Props> = ({
  onBack,
  onTabPress,
  onLogout,
  memberships,
  activeMembership,
  onMembershipChange,
}) => {
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [pickupPrefs, setPickupPrefs] = useState<CustomerPickupBusinessPrefs>(() => parseCustomerPickupPrefs(undefined));
  const [expandedAppointmentId, setExpandedAppointmentId] = useState<string | null>(null);

  const canUpdateStatus = !!activeMembership?.permissions?.appointments?.updateStatus;
  const isWorker = activeMembership?.role === 'worker';

  const title = useMemo(() => {
    if (!activeMembership) return 'Work';
    if (activeMembership.role === 'site_manager') return 'Work — Site Manager';
    if (activeMembership.role === 'worker') return 'Work — My Jobs';
    return 'Work';
  }, [activeMembership]);

  const filteredAppointments = useMemo(() => {
    if (!activeMembership) return [];
    const businessId = activeMembership.businessId;
    const locIds = activeMembership.locationIds || [];
    const workerId = activeMembership.workerId;

    return (appointments || [])
      .filter((a) => (a as any).businessOwnerId === businessId)
      .filter((a) => {
        const aLoc = (a as any).locationId as string | undefined;
        if (locIds.length === 0) return true; // not site-scoped yet
        if (!aLoc) return true; // legacy appointments (no location) are visible
        return locIds.includes(aLoc);
      })
      .filter((a) => {
        if (!isWorker) return true;
        if (!workerId) return false;
        return (a as any).workerId === workerId;
      })
      .sort((a, b) => a.appointmentDate.localeCompare(b.appointmentDate) || (a.appointmentTime || '').localeCompare(b.appointmentTime || ''));
  }, [appointments, activeMembership, isWorker]);

  const appointmentDateSections = useMemo(
    () => groupAppointmentsIntoDateSections(filteredAppointments, { descending: false }),
    [filteredAppointments]
  );

  useEffect(() => {
    setExpandedAppointmentId(null);
  }, [activeMembership?.businessId, activeMembership?.workerId]);

  const queueInsights = useMemo(
    () =>
      appointmentManager.computeQueueInsights(filteredAppointments, {
        businessOwnerId: activeMembership?.businessId,
        workerId: isWorker ? activeMembership?.workerId : undefined,
      }),
    [filteredAppointments, activeMembership?.businessId, activeMembership?.workerId, isWorker]
  );

  const load = async () => {
    try {
      setLoading(true);
      const all = await appointmentManager.getAppointments();
      setAppointments(all || []);
    } catch (e) {
      console.error('WorkAppointments load error:', e);
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [activeMembership?.businessId, activeMembership?.workerId]);

  useEffect(() => {
    let cancelled = false;
    const bid = activeMembership?.businessId;
    if (!bid) {
      setPickupPrefs(parseCustomerPickupPrefs(undefined));
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      try {
        if (Platform.OS === 'web') {
          const { doc, getDoc } = await import('firebase/firestore');
          const { getWebDb, initializeFirebase } = await import('../services/unifiedAuthService');
          if (!initializeFirebase()) return;
          const db = getWebDb();
          if (!db) return;
          const snap = await getDoc(doc(db, 'businesses', bid));
          const exists =
            typeof (snap as any).exists === 'function' ? (snap as any).exists() : !!(snap as any).exists;
          const data = exists && typeof snap.data === 'function' ? snap.data() : {};
          if (!cancelled) setPickupPrefs(parseCustomerPickupPrefs(data as Record<string, unknown>));
        } else {
          const { initializeFirebase } = await import('../services/unifiedAuthService');
          initializeFirebase();
          const { getApp } = require('@react-native-firebase/app');
          const { getFirestore, doc, getDoc } = require('@react-native-firebase/firestore');
          const db = getFirestore(getApp());
          const snap = await getDoc(doc(db, 'businesses', bid));
          const exists =
            typeof (snap as any).exists === 'function' ? (snap as any).exists() : !!(snap as any).exists;
          const data = exists && typeof snap.data === 'function' ? snap.data() : {};
          if (!cancelled) setPickupPrefs(parseCustomerPickupPrefs(data as Record<string, unknown>));
        }
      } catch {
        if (!cancelled) setPickupPrefs(parseCustomerPickupPrefs(undefined));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeMembership?.businessId]);

  const confirmComplete = (apt: Appointment) => {
    if (!canUpdateStatus) {
      Alert.alert('Not allowed', 'You do not have permission to update appointment status.');
      return;
    }
    const pickup = pickupPrefs.pickupReadyDisplayEnabled;
    Alert.alert(
      pickup ? 'Ready for pickup' : 'Mark complete',
      pickup
        ? 'Notify the customer (in app) that their visit is ready for pickup?'
        : 'Mark this appointment as completed?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: pickup ? 'Mark ready' : 'Complete',
          style: 'default',
          onPress: async () => {
            try {
              await appointmentManager.updateAppointmentStatus(apt.id, 'completed');
              if (
                pickupPrefs.pickupReadyDisplayEnabled &&
                pickupPrefs.pickupReadySmsEnabled &&
                (apt.phone || '').trim()
              ) {
                try {
                  const { smsService } = await import('../services/smsService');
                  const place = apt.businessName || 'us';
                  const msg = `Hi ${apt.customerName || 'there'}, your ${apt.serviceType || 'appointment'} at ${place} is ready for pickup. Reply STOP to opt out.`;
                  await smsService.sendSMS((apt.phone || '').trim(), msg, 'general');
                } catch (smsErr) {
                  console.warn('Pickup-ready SMS failed:', smsErr);
                }
              }
              await load();
            } catch (e) {
              Alert.alert('Error', 'Could not update status. Please try again.');
            }
          },
        },
      ]
    );
  };

  const updateQueueStage = async (apt: Appointment, action: 'checked_in' | 'in_service' | 'completed' | 'no_show' | 'delay') => {
    if (!canUpdateStatus) {
      Alert.alert('Not allowed', 'You do not have permission to update appointment status.');
      return;
    }
    try {
      const nowIso = new Date().toISOString();
      if (action === 'checked_in') {
        await appointmentManager.updateAppointmentQueueFields(apt.id, { queueStage: 'checked_in', checkedInAt: nowIso });
      } else if (action === 'in_service') {
        await appointmentManager.updateAppointmentQueueFields(apt.id, { queueStage: 'in_service', serviceStartedAt: nowIso });
      } else if (action === 'completed') {
        await appointmentManager.updateAppointmentQueueFields(apt.id, { queueStage: 'completed', serviceCompletedAt: nowIso });
        await appointmentManager.updateAppointmentStatus(apt.id, 'completed');
      } else if (action === 'no_show') {
        await appointmentManager.updateAppointmentQueueFields(apt.id, { queueStage: 'no_show' });
        await appointmentManager.updateAppointmentStatus(apt.id, 'cancelled');
      } else if (action === 'delay') {
        const nextDelay = (apt.delayMinutes ?? 0) + 10;
        await appointmentManager.updateAppointmentQueueFields(apt.id, {
          delayMinutes: nextDelay,
          delayReason: 'Running late',
        });
      }
      await load();
    } catch (e) {
      Alert.alert('Error', 'Could not update queue state. Please try again.');
    }
  };

  const getLiveQueueState = (apt: Appointment): 'show' | 'future' | 'hide' => {
    const todayKey = toLocalDateKey(new Date().toISOString());
    const aptDateKey = toLocalDateKey(apt.appointmentDate);
    if (!aptDateKey || aptDateKey !== todayKey) {
      return aptDateKey > todayKey ? 'future' : 'hide';
    }
    if (apt.status === 'completed' || apt.status === 'cancelled') return 'hide';
    if (apt.queueStage === 'completed' || apt.queueStage === 'no_show') return 'hide';
    return 'show';
  };

  if (!activeMembership) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Work</Text>
        </View>
        <View style={styles.card}>
          <Text style={styles.emptyTitle}>No workspace selected</Text>
          <Text style={styles.emptyText}>Accept an invite or ask an owner to add you to a business.</Text>
          <TouchableOpacity style={styles.primaryButton} onPress={onBack}>
            <Text style={styles.primaryButtonText}>Back</Text>
          </TouchableOpacity>
        </View>
        <UserBottomMenu activeTab="work" showWorkTab={memberships.length > 0} onTabPress={onTabPress} onLogout={onLogout} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={{ flex: 1 }}>
          <Text style={styles.headerTitle}>{title}</Text>
          <Text style={styles.headerSubtitle}>Showing {filteredAppointments.length} appointments</Text>
        </View>
        <TouchableOpacity style={styles.refreshButton} onPress={load}>
          <Text style={styles.refreshButtonText}>Refresh</Text>
        </TouchableOpacity>
      </View>

      {memberships.length > 1 && (
        <ScrollView horizontal style={styles.workspaceRow} showsHorizontalScrollIndicator={false}>
          {memberships.map((m) => {
            const selected = m.businessId === activeMembership.businessId;
            const label = m.role === 'site_manager' ? 'Manager' : m.role === 'worker' ? 'Worker' : m.role;
            return (
              <TouchableOpacity
                key={`${m.businessId}_${m.role}_${m.workerId ?? ''}`}
                style={[styles.workspacePill, selected && styles.workspacePillActive]}
                onPress={() => onMembershipChange(m)}
              >
                <Text style={[styles.workspacePillText, selected && styles.workspacePillTextActive]}>
                  {m.businessId.slice(0, 6)}… · {label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      )}

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator size="large" color="#007AFF" />
          <Text style={styles.loadingText}>Loading work appointments…</Text>
        </View>
      ) : (
        <ScrollView style={styles.content} contentContainerStyle={{ paddingBottom: 110 }}>
          {filteredAppointments.length === 0 ? (
            <View style={styles.card}>
              <Text style={styles.emptyTitle}>No appointments</Text>
              <Text style={styles.emptyText}>
                {isWorker ? 'No appointments are assigned to you yet.' : 'No appointments found for this workspace.'}
              </Text>
            </View>
          ) : (
            appointmentDateSections.map((section) => (
              <View key={section.dateKey} style={styles.dateSectionBlock}>
                <Text style={styles.dateSectionHeading}>{section.title}</Text>
                <Text style={styles.dateSectionCount}>
                  {section.items.length} booking{section.items.length === 1 ? '' : 's'}
                </Text>
                {section.items.map((apt) => {
                  const expanded = expandedAppointmentId === apt.id;
                  return (
                    <View key={apt.id} style={styles.aptCard}>
                      <TouchableOpacity
                        style={styles.compactAppointmentRow}
                        activeOpacity={0.65}
                        onPress={() => setExpandedAppointmentId((cur) => (cur === apt.id ? null : apt.id))}
                      >
                        <View style={styles.compactLeft}>
                          <Text style={styles.compactTime}>{apt.appointmentTime || '—'}</Text>
                          <Text style={styles.aptTitle} numberOfLines={1}>
                            {apt.customerName}
                          </Text>
                          <Text style={styles.aptMeta} numberOfLines={1}>
                            {apt.serviceType}
                          </Text>
                        </View>
                        <View style={styles.compactRight}>
                          <View style={styles.compactBadgesRow}>
                            <View style={styles.statusMini}>
                              <Text style={styles.statusMiniText}>{apt.status}</Text>
                            </View>
                            <AppointmentPrepaidBadge appointment={apt} variant="pill" />
                          </View>
                          <Text style={styles.compactTapHint}>{expanded ? 'Tap to hide' : 'Tap for details'}</Text>
                        </View>
                      </TouchableOpacity>
                      {expanded && (
                        <View style={styles.expandedBlock}>
                          <AppointmentPrepaidBadge appointment={apt} variant="banner" />
                          <View style={styles.expandedInnerRow}>
                            <View style={{ flex: 1, minWidth: 0 }}>
                              <Text style={styles.aptMeta}>
                                {apt.appointmentDate} at {apt.appointmentTime} · Status: {apt.status}
                              </Text>
                              {getLiveQueueState(apt) === 'show' && !!queueInsights[apt.id] && (
                                <LiveQueueCard insight={queueInsights[apt.id]} />
                              )}
                              {getLiveQueueState(apt) === 'future' && (
                                <Text style={styles.aptMeta}>Live Queue will be available on the appointment day.</Text>
                              )}
                              <Text style={styles.aptMeta}>
                                Stage: {apt.queueStage || 'booked'}
                                {(apt.delayMinutes ?? 0) > 0 ? ` · Delay +${apt.delayMinutes}m` : ''}
                              </Text>
                              {!!apt.phone && <Text style={styles.aptMeta}>📞 {apt.phone}</Text>}
                            </View>
                            {canUpdateStatus && apt.status !== 'completed' && apt.status !== 'cancelled' && (
                              <View style={styles.actionsCol}>
                                <TouchableOpacity style={styles.smallButton} onPress={() => updateQueueStage(apt, 'checked_in')}>
                                  <Text style={styles.smallButtonText}>Check-in</Text>
                                </TouchableOpacity>
                                <TouchableOpacity style={styles.smallButton} onPress={() => updateQueueStage(apt, 'in_service')}>
                                  <Text style={styles.smallButtonText}>Start</Text>
                                </TouchableOpacity>
                                <TouchableOpacity style={styles.smallButton} onPress={() => updateQueueStage(apt, 'delay')}>
                                  <Text style={styles.smallButtonText}>+10m</Text>
                                </TouchableOpacity>
                                <TouchableOpacity style={styles.completeButton} onPress={() => confirmComplete(apt)}>
                                  <Text style={styles.completeButtonText}>
                                    {pickupPrefs.pickupReadyDisplayEnabled ? 'Ready' : 'Complete'}
                                  </Text>
                                </TouchableOpacity>
                                <TouchableOpacity style={styles.noShowButton} onPress={() => updateQueueStage(apt, 'no_show')}>
                                  <Text style={styles.completeButtonText}>No-show</Text>
                                </TouchableOpacity>
                              </View>
                            )}
                          </View>
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>
            ))
          )}
        </ScrollView>
      )}

      <UserBottomMenu activeTab="work" showWorkTab={memberships.length > 0} onTabPress={onTabPress} onLogout={onLogout} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F2F2F7' },
  header: {
    paddingTop: 54,
    paddingHorizontal: 16,
    paddingBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5E7',
  },
  headerTitle: { fontSize: 18, fontWeight: '700', color: '#1C1C1E' },
  headerSubtitle: { marginTop: 4, fontSize: 12, color: '#8E8E93' },
  refreshButton: { paddingHorizontal: 10, paddingVertical: 8, borderRadius: 10, backgroundColor: '#F2F2F7' },
  refreshButtonText: { color: '#007AFF', fontWeight: '600' },
  workspaceRow: { paddingHorizontal: 12, paddingVertical: 10, backgroundColor: '#fff' },
  workspacePill: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 999, backgroundColor: '#F2F2F7', marginRight: 8 },
  workspacePillActive: { backgroundColor: '#007AFF' },
  workspacePillText: { fontSize: 12, color: '#3A3A3C', fontWeight: '600' },
  workspacePillTextActive: { color: '#fff' },
  content: { flex: 1, padding: 16 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: 12, color: '#8E8E93' },
  card: { backgroundColor: '#fff', borderRadius: 14, padding: 16, borderWidth: 1, borderColor: '#E5E5E7' },
  emptyTitle: { fontSize: 16, fontWeight: '700', color: '#1C1C1E', marginBottom: 6, textAlign: 'center' },
  emptyText: { fontSize: 14, color: '#8E8E93', textAlign: 'center', marginBottom: 14 },
  primaryButton: { backgroundColor: '#007AFF', paddingVertical: 12, borderRadius: 10, alignItems: 'center' },
  primaryButtonText: { color: '#fff', fontWeight: '700' },
  aptCard: {
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: 14,
    borderWidth: 1,
    borderColor: '#E5E5E7',
    marginBottom: 12,
  },
  dateSectionBlock: {
    marginBottom: 4,
  },
  dateSectionHeading: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1C1C1E',
    marginBottom: 2,
  },
  dateSectionCount: {
    fontSize: 12,
    color: '#8E8E93',
    marginBottom: 10,
  },
  compactAppointmentRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
  },
  compactLeft: { flex: 1, minWidth: 0 },
  compactTime: { fontSize: 16, fontWeight: '700', color: '#1C1C1E', marginBottom: 4 },
  compactRight: { alignItems: 'flex-end', maxWidth: '46%' },
  compactBadgesRow: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 6, alignItems: 'center' },
  compactTapHint: { marginTop: 6, fontSize: 11, color: '#8E8E93' },
  statusMini: {
    backgroundColor: '#E5E5EA',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  statusMiniText: { fontSize: 11, fontWeight: '700', color: '#3A3A3C' },
  expandedBlock: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: '#E5E5E7',
  },
  expandedInnerRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  aptTitle: { fontSize: 15, fontWeight: '700', color: '#1C1C1E', marginBottom: 4 },
  aptMeta: { fontSize: 12, color: '#6C6C70', marginBottom: 2 },
  completeButton: { backgroundColor: '#28a745', paddingHorizontal: 12, paddingVertical: 10, borderRadius: 10 },
  completeButtonText: { color: '#fff', fontWeight: '800', fontSize: 12 },
  actionsCol: { gap: 6 },
  smallButton: { backgroundColor: '#007AFF', paddingHorizontal: 10, paddingVertical: 8, borderRadius: 8 },
  smallButtonText: { color: '#fff', fontWeight: '700', fontSize: 11 },
  noShowButton: { backgroundColor: '#B42318', paddingHorizontal: 12, paddingVertical: 10, borderRadius: 10 },
});

export default WorkAppointmentsScreen;

